import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
	ConfigProvider,
	Form,
	Input,
	Card,
	Row,
	Col,
	Tag,
	Tooltip,
	Select,
	Space,
	DatePicker
} from 'antd'
import {
	WarningOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'
import { runInAction, toJS } from 'mobx'
import { IS_NEW } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { SubmitButton } from '../../../components/Form'
import { FormatedTimestamp } from '../../../components/Shared'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actSaveMeeting,
	requireMeetingById
} from '../../../stores/meetings/actions'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import dayjs from 'dayjs'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const timeOptions = [
	'09:00 AM',
	'09:30 AM',
	'10:00 AM',
	'10:30 AM',
	'11:00 AM',
	'11:30 AM',
	'12:00 PM',
	'12:30 PM',
	'01:00 PM',
	'01:30 PM',
	'02:00 PM',
	'02:30 PM',
	'03:00 PM',
	'03:30 PM',
	'04:00 PM',
	'04:30 PM',
	'05:00 PM'
].map((time) => ({
	key: `time-${time.replace(/\s+/g, '-').toLowerCase()}`,
	label: time,
	value: time
}))

/**
 * @component MeetingEdit
 * @description Allows creation and editing of meeting details. Supports form validation, guided UI tours, real-time availability checking, and sync on interval toggle.
 * Fields include participant name, email, phone, date, time, and notes. Renders a form in either editable or read-only (viewOnly) mode.
 */
const MeetingEdit = ({ viewOnly: viewOnlyProp }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const location = useLocation()
	const { meetings, users, tourStore } = useStores()
	const { loggedInUserId, loggedInUser } = users

	const viewOnly =
		typeof viewOnlyProp !== 'undefined'
			? viewOnlyProp
			: location.pathname.includes('/view')
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const _itm = requireMeetingById(itmId)
	const [form] = Form.useForm()
	const [selectedDate, setSelectedDate] = useState(_itm?.date || null)

	const selfPermission = isAdmin || isSuperAdmin
	if (!selfPermission && _itm?.loaded !== IS_NEW) {
		viewOnly = true
	}

	/**
	 * @function generateInitialFormValues
	 * @description Returns the initial values for the meeting form based on the current meeting store item.
	 * @returns {Object} Form default values.
	 */
	const generateInitialFormValues = () => ({
		name: _itm.name,
		email: _itm.email,
		phone: _itm.phone,
		date: _itm.date ? dayjs(_itm.date, 'YYYY-MM-DD') : null,
		available_hour: _itm.hour,
		message: _itm.note,
		created_by: _itm.createdBy || loggedInUserId
	})

	/**
	 * @function _doSave
	 * @description Handles form submission by setting meeting values in the MobX store and persisting them to the server.
	 * @param {Object} vals - Form field values.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				_itm.setName(vals.name)
				_itm.setEmail(vals.email)
				_itm.setPhone(vals.phone)
				_itm.setDate(vals.date.format('YYYY-MM-DD'))
				_itm.setHour(vals.available_hour)
				_itm.setNote(vals.message)
				_itm.setCreatedBy(loggedInUserId)
			})

			actSaveMeeting(itmId, toJS(_itm.full))
				.then(() => {
					navigate('/meetings')
				})
				.catch(console.error)
		}
	}

	/**
	 * @function handleChangeDate
	 * @description Updates selected date in local state to recalculate available time slots.
	 * @param {dayjs.Dayjs} date - Selected date object.
	 * @param {string} dateString - Formatted string value of the selected date.
	 */
	const handleChangeDate = (date, dateString) => {
		setSelectedDate(dateString)
	}

	/**
	 * @function renderTimeOptions
	 * @description Generates a list of time options for the Select dropdown, disabling already booked slots.
	 * @returns {Array} Array of Select-compatible time option objects.
	 */
	const renderTimeOptions = () => {
		return timeOptions.map((time) => {
			const isBooked =
				selectedDate &&
				meetings.meetings.some(
					(m) =>
						m.date === selectedDate &&
						m.hour === time.value &&
						m.id !== itmId
				)
			return {
				...time,
				disabled: isBooked,
				label: `${time.label} ${isBooked ? '(Booked)' : ''}`
			}
		})
	}

	const tourForm = getTourArrayByKeys('form', ['section', 'name'])

	/**
	 * @function startTour
	 * @description Launches the guided UI tour for the full Meeting Edit interface including metadata and form controls.
	 */
	const startTour = () => {
		tourStore.startTour('MeetingEdit-tour', [
			!!!_itm.to_delete && {
				title: 'Sync on interval',
				description:
					'Enable or disable data synchronization on interval',
				target: () => document.getElementById('target-interval-itm')
			},
			...getTourArrayByKeys('info', ['name', 'modified']),
			...getTourArrayByKeys('form', ['section'])
		])
	}

	/**
	 * @function startTourForm
	 * @description Starts a tour specific to the form fields (name, date, time, etc.) within the meeting editor.
	 */
	const startTourForm = () => {
		tourStore.startTour('MeetingEdit-tour', [
			...tourForm,
			...getTourArrayByKeys('meetingForm', ['all'])
		])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_meeting') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}
								<FormatedTimestamp timestamp={_itm.to_delete} />
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_meeting')}>
								<EyeOutlined id="target-mode" /> {_itm.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create%_meeting')
						) : (
							<Tooltip title={t('__edit%')}>
								<SettingOutlined /> {_itm.name}
							</Tooltip>
						)}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="meeting"
						initialValues={generateInitialFormValues()}
						onFinish={_doSave}
					>
						<Space style={{ width: '100%' }} direction="vertical">
							<GeneralTourTitle
								align="end"
								titleProps={{
									text: 'General',
									level: 5
								}}
								buttonProps={{
									onClick: startTourForm,
									disabled: false,
									style: { marginBottom: 4 }
								}}
							/>

							<Row id="target-form" gutter={[50, 50]}>
								<Col span={24}>
									<Row gutter={[20, 20]}>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-name"
										>
											<Form.Item
												label={t('single.name')}
												tooltip={t(
													'__enter.participant%single.name'
												)}
												name="name"
												rules={[
													{
														required: true,
														message: t(
															'__enter.please%single.name'
														)
													}
												]}
											>
												<Input disabled={viewOnly} />
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label="Email"
												tooltip={t(
													'__enter.participant%single.email'
												)}
												name="email"
												rules={[
													{
														required: true,
														message: t(
															'__enter.please%single.email'
														)
													},
													{
														type: 'email',
														message: t(
															'__enter.please%_email.valid'
														)
													}
												]}
											>
												<Input disabled={viewOnly} />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[20, 20]}>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-phone"
										>
											<Form.Item
												label={t('single.phone')}
												tooltip={t(
													'__enter.participant%single.phone'
												)}
												name="phone"
												rules={[
													{
														required: true,
														message: t(
															'__enter.please%single.phone'
														)
													}
												]}
											>
												<Input disabled={viewOnly} />
											</Form.Item>
										</Col>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-date"
										>
											<Form.Item
												label={t('single.date')}
												tooltip={t(
													'__select.item%single.date'
												)}
												name="date"
												rules={[
													{
														required: true,
														message: t(
															'__select.item%single.date'
														)
													}
												]}
											>
												<DatePicker
													onChange={handleChangeDate}
													style={{ width: '100%' }}
													disabled={viewOnly}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[20, 20]}>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-time"
										>
											<Form.Item
												label={t('single.time')}
												tooltip={t(
													'__select.item%single.time'
												)}
												name="available_hour"
												rules={[
													{
														required: true,
														message:
															t('single.time')
													}
												]}
											>
												<Select
													options={renderTimeOptions()}
													disabled={viewOnly}
												/>
											</Form.Item>
										</Col>
										<Col
											xs={24}
											sm={24}
											md={12}
											lg={12}
											id="target-form-message"
										>
											<Form.Item
												label={t('single.message')}
												tooltip={t(
													'__add%_meeting.notes'
												)}
												name="message"
											>
												<Input.TextArea
													rows={4}
													disabled={viewOnly}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Col>
							</Row>
							{!viewOnly && (
								<Row gutter={[10, 10]} align="bottom">
									<Col>
										<SubmitButton
											disabled={!selfPermission}
										>
											{_itm.loaded === IS_NEW
												? t('single.create')
												: t('single.update')}
										</SubmitButton>
									</Col>
								</Row>
							)}
						</Space>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}

MeetingEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(MeetingEdit)
